import { useState } from "react";
import { Link } from "react-router-dom";
import { X, Loader2, CheckCircle2, ArrowRight, Building2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const EMPLOYEE_RANGES = ["1-25", "26-100", "101-500", "501-2,000", "2,000+"];

export default function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", employees: "26-100", message: "" });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to the Terms of Service & Service Agreement");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, terms_accepted: true });
      setSubmitted(true);
      toast.success("Got it! We'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setSubmitted(false);
    setAgreed(false);
    setForm({ name: "", email: "", company: "", employees: "26-100", message: "" });
    onClose();
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-900/70 backdrop-blur-sm fade-up"
      onClick={close}
      data-testid="contact-modal-backdrop"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-lg shadow-2xl relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="contact-modal"
      >
        <button
          onClick={close}
          className="absolute top-5 right-5 p-2 rounded-full text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition z-10"
          data-testid="contact-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="p-10 text-center" data-testid="contact-modal-success">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={2} />
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl text-zinc-900 mb-3">
              Thanks, {form.name.split(" ")[0]}!
            </h2>
            <p className="text-zinc-500 mb-8 leading-relaxed">
              We received your request. Our team will reach out to <b className="text-zinc-900">{form.email}</b> within 1 business day with custom pricing and a tailored onboarding plan.
            </p>
            <button
              onClick={close}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white font-bold px-6 py-3 rounded-full hover:bg-zinc-800 transition"
              data-testid="contact-modal-done"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-8 sm:p-10">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600">Enterprise inquiry</div>
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl text-zinc-900 mb-2">
              Let's build your IT helpdesk.
            </h2>
            <p className="text-zinc-500 mb-7 text-sm">
              Tell us about your team — we'll send custom pricing & a setup plan within 1 business day.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Your name</label>
                  <input
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Alex Rivera"
                    className="mt-1.5 w-full rounded-xl border-2 border-zinc-200 px-3.5 py-2.5 text-sm focus:border-blue-600 focus:outline-none transition"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Work email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="alex@company.com"
                    className="mt-1.5 w-full rounded-xl border-2 border-zinc-200 px-3.5 py-2.5 text-sm focus:border-blue-600 focus:outline-none transition"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Company</label>
                <input
                  required
                  value={form.company}
                  onChange={onChange("company")}
                  placeholder="Acme Inc."
                  className="mt-1.5 w-full rounded-xl border-2 border-zinc-200 px-3.5 py-2.5 text-sm focus:border-blue-600 focus:outline-none transition"
                  data-testid="contact-company"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold tracking-widest uppercase text-zinc-500 mb-1.5 block">Team size</label>
                <div className="grid grid-cols-5 gap-1.5">
                  {EMPLOYEE_RANGES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setForm({ ...form, employees: r })}
                      className={`py-2 rounded-lg text-xs font-bold transition ${
                        form.employees === r
                          ? "bg-zinc-900 text-white"
                          : "bg-white border-2 border-zinc-200 text-zinc-600 hover:border-zinc-400"
                      }`}
                      data-testid={`contact-employees-${r}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Anything we should know? <span className="normal-case text-zinc-400 font-medium">(optional)</span></label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Current IT tool, biggest pain points, target launch date…"
                  className="mt-1.5 w-full rounded-xl border-2 border-zinc-200 px-3.5 py-2.5 text-sm focus:border-blue-600 focus:outline-none transition resize-none"
                  data-testid="contact-message"
                />
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer pt-1" data-testid="contact-terms-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-zinc-300 accent-blue-600 cursor-pointer"
                  data-testid="contact-terms-checkbox"
                />
                <span className="text-xs text-zinc-500 leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-blue-600 hover:underline font-medium">Terms of Service</Link>{" "}
                  &{" "}
                  <Link to="/service-agreement" target="_blank" className="text-blue-600 hover:underline font-medium">Service Agreement</Link>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !agreed}
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white font-bold py-3.5 rounded-full hover:bg-zinc-800 disabled:opacity-50 transition-all hover:-translate-y-0.5 mt-2"
                data-testid="contact-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send inquiry <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-[10px] text-zinc-400 text-center mt-2">
                We never share your info. Response within 1 business day.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
